import cn from "classnames";
import type { Pead } from "epsm-api-types/models";
import type React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
	formatValueDate,
	formatValuePercent,
} from "@/components/widgets/helpers/FormattingHelpers";
import { daysCounter } from "@/shared/helpers/daysCounter";
import { roundToTwoDecimals } from "@/shared/helpers/roundToTwoDecimals/roundToTwoDecimals";
import { getDriftTypeDescription } from "../../../lib/helpers/getDriftTypeDescription/getDriftTypeDescription";
import type { DriftType } from "../../../types";

type Props = {
	activePead: Pead | null;
	relativeToSpy: boolean;
	setPeadState: () => void;
};

export const CurrentPeadData: React.FC<Props> = ({
	activePead,
	relativeToSpy,
	setPeadState,
}) => {
	const spreadToDrift = roundToTwoDecimals(activePead?.spread_to_drift || 0);
	const spreadToDriftPct = formatValuePercent(activePead?.spread_to_drift_pct);
	const relativeSpreadToDrift = roundToTwoDecimals(
		activePead?.relative_spread_to_drift || 0,
	);
	const relativeSpreadToDriftPct = formatValuePercent(
		activePead?.relative_spread_to_drift_pct,
	);

	if (!activePead) {
		return (
			<div className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
				{"PEAD is not available for now"}
			</div>
		);
	}

	return (
		<div>
			<h1>Current PEAD data</h1>

			<table
				className="w-full border-2 border-gray bg-background"
				data-testid="current-pead-data"
			>
				<thead className="border border-light-gray">
					<tr className="text-left">
						<td
							className="text-light-gray font-normal w-fit whitespace-nowrap"
							colSpan={6}
						>
							Drift C:{" "}
							{getDriftTypeDescription(
								activePead?.drift_unit?.value as DriftType,
							)}
						</td>
					</tr>

					<tr className="text-left">
						<th scope="row" className="text-light-gray font-normal">
							Last Earning Date:
						</th>
						<td className="text-light">
							{formatValueDate(activePead.earning_day)}
						</td>
						<th scope="row" className="text-light-gray font-normal">
							# Days Since:
						</th>
						<td className="text-light-gray">
							{daysCounter(activePead.earning_day, "since")}
						</td>
					</tr>

					<tr className="text-left">
						<th scope="row" className="text-light-gray font-normal">
							Next Earning Date:
						</th>
						<td className="text-light-gray">
							{formatValueDate(activePead.next_earning_day)}
						</td>
						<th scope="row" className="text-light-gray font-normal">
							# Days to:
						</th>
						<td className="text-light-gray">
							{daysCounter(activePead.next_earning_day, "to")}
						</td>
					</tr>

					<tr className="text-left">
						<th scope="row" className="text-light-gray font-normal">
							Spread to Drift
						</th>

						<td
							className={cn(" text-red-500", {
								"text-success-light": spreadToDrift > 0,
							})}
						>{`${spreadToDrift}$`}</td>
						<td
							className={cn("text-red-500", {
								"text-success-light": Number.parseFloat(spreadToDriftPct) > 0,
							})}
						>{`${spreadToDriftPct}`}</td>
					</tr>

					<tr className="text-left">
						<th scope="row" className="text-light-gray font-normal">
							Relative Spread to drift
						</th>

						<td
							className={cn(" text-red-500", {
								"text-success-light": relativeSpreadToDrift > 0,
							})}
						>{`${relativeSpreadToDrift}$`}</td>
						<td
							className={cn("text-red-500", {
								"text-success-light":
									Number.parseFloat(relativeSpreadToDriftPct) > 0,
							})}
						>{`${relativeSpreadToDriftPct}`}</td>

						<td colSpan={3} className="text-right">
							<div className="inline-flex gap-1 items-center">
								<Checkbox
									className="bg-background border border-light-gray rounded px-0.5 data-[state=checked]:text-white"
									id="dev"
									onClick={setPeadState}
									checked={relativeToSpy}
								/>

								<label
									htmlFor="dev"
									className="cursor-pointer text-light-gray font-normal"
								>
									Relative to SPY
								</label>
							</div>
						</td>
					</tr>
				</thead>
			</table>
		</div>
	);
};
